import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import useTrans from '@hooks/useTrans'
import { useDebounceCallback } from '@hooks/useDebounceCallback'
import { cn } from '@utils/safeTs'
import { EditorColoreApiType } from '@utils/types'
import FormField from '../FormField/FormField'
import FormInput from '../FormInput/FormInput'
import PantoneSelect from '../PantoneSelect/PantoneSelect'
import styles from './EditorColor.module.sass'
import {
  applyAlphaToHexColor,
  convertHexToCmyk,
  normalizeHexColor,
  splitHexColorAndAlpha,
} from '@utils/editor-bozze'
import { getPantoneHex, toAlphaHex } from '@utils/editor-bozze/colors'
import { convertFogra39HexToCmyk } from '@utils/editor-bozze/fogra39-api'
import FormSelect from '../FormSelect/FormSelect'
import SketchColorPicker from '../SketchColorPicker/SketchColorPicker'
import GithubColorPicker from '../GithubColorPicker/GithubColorPicker'

interface Props {
  id: string
  variant: 'pantone' | 'cmyk'
  value: string // hex
  onChange: (e: any) => void
  allowedColors?: EditorColoreApiType[]
  lockColorChange?: EditorColoreApiType | null
  className?: string
}

const EditorColor = (props: Props) => {
  const {
    id,
    variant = 'pantone',
    value,
    onChange,
    allowedColors = [],
    lockColorChange = null,
    className = '',
  } = props

  const t = useTrans()
  const rootRef = useRef(null)
  const colorPickerRef = useRef(null)
  const colorBoxButtonRef = useRef(null)
  const triggerRef = useRef(null)
  const [colorPickerOpen, setColorPickerOpen] = useState(false)
  const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 })
  const [isFogra39Admissible, setIsFogra39Admissible] = useState<boolean | null>(null)
  const gamutCheckRequestIdRef = useRef(0)
  const gamutCheckAbortRef = useRef<AbortController | null>(null)

  const normalizedAllowedHexColors = useMemo(
    () => {
      const normalizedAllowed = (allowedColors || [])
        .map((color) => normalizeHexColor(color?.hex || ''))
        .filter(Boolean)

      const variantAllowed =
        variant === 'pantone' ? normalizedAllowed.map((hex) => getPantoneHex(hex)) : normalizedAllowed

      return Array.from(new Set(variantAllowed.map((hex) => normalizeHexColor(hex)).filter(Boolean)))
    },
    [allowedColors, variant]
  )
  const hasLimitedPalette = normalizedAllowedHexColors.length > 0
  const isColorChangeDisabled = !!lockColorChange
  const valueAsLocked = isColorChangeDisabled
    ? variant === 'pantone'
      ? getPantoneHex(lockColorChange.hex)
      : normalizeHexColor(lockColorChange.hex)
    : null

  const updateColorPickerPosition = () => {
    const anchor = triggerRef?.current || colorBoxButtonRef?.current || rootRef?.current
    if (!anchor) return

    // Posizioniamo rispetto al componente completo (color box + field), non solo al trigger.
    const viewportLeftPadding = 32
    const viewportTopPadding = 8
    const dropdownWidth = colorPickerRef?.current?.offsetWidth || 220
    const dropdownHeight = colorPickerRef?.current?.offsetHeight || 320
    const rect = anchor.getBoundingClientRect()
    const rootRect = rootRef?.current?.getBoundingClientRect?.()
    const rootTop = rootRect?.top ?? rect.top

    let top = rect.bottom + viewportTopPadding
    let left = rootRect?.left ?? rect.left

    const overflowRight = left + dropdownWidth + viewportLeftPadding - window.innerWidth
    if (overflowRight > 0) {
      left = Math.max(viewportLeftPadding, left - overflowRight)
    }

    if (left < viewportLeftPadding) {
      left = viewportLeftPadding
    }

    const spaceBelow = window.innerHeight - rect.bottom - viewportTopPadding
    const spaceAbove = rootTop - viewportTopPadding
    // Flip automatico: se sotto manca spazio e sopra è meglio, apriamo sopra.
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      top = rootTop - dropdownHeight - viewportTopPadding
    }

    if (top + dropdownHeight > window.innerHeight - viewportTopPadding) {
      top = Math.max(viewportTopPadding, window.innerHeight - dropdownHeight - viewportTopPadding)
    }

    if (top < viewportTopPadding) {
      top = viewportTopPadding
    }

    setColorPickerPosition({
      top,
      left,
    })
  }

  const renderColorBox = () => {
    const currentColor = valueAsLocked || value
    const { alphaHex, hasAlpha } = splitHexColorAndAlpha(currentColor)
    const isTransparent = hasAlpha && alphaHex !== 'ff'
    return (
      <div
        className={`${styles.colorBox} ${isTransparent ? styles['colorBox--transparent'] : ''}`}
        style={{ backgroundColor: currentColor || 'transparent' }}
      />
    )
  }

  useEffect(() => {
    if (
      colorPickerOpen &&
      colorPickerRef?.current &&
      triggerRef?.current &&
      colorBoxButtonRef?.current
    ) {
      const handleOutsideClick = (e) => {
        if (
          !colorPickerRef.current.contains(e.target) &&
          !triggerRef.current.contains(e.target) &&
          triggerRef.current !== e.target &&
          !colorBoxButtonRef.current.contains(e.target) &&
          colorBoxButtonRef.current !== e.target
        ) {
          e.preventDefault()
          setColorPickerOpen(false)
        }
      }
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          setColorPickerOpen(false)
        }
      }
      const handleFocusOut = (e) => {
        if (!colorPickerRef.current.contains(e.target)) {
          e.preventDefault()
          setColorPickerOpen(false)
        }
      }
      window.addEventListener('click', handleOutsideClick)
      window.addEventListener('keydown', handleEscape)
      window.addEventListener('focusin', handleFocusOut)
      return () => {
        window.removeEventListener('click', handleOutsideClick)
        window.removeEventListener('keydown', handleEscape)
        window.removeEventListener('focusin', handleFocusOut)
      }
    }
  }, [colorPickerOpen, colorPickerRef?.current, triggerRef?.current, colorBoxButtonRef?.current])

  useEffect(() => {
    if (!colorPickerOpen) return

    updateColorPickerPosition()
    window.addEventListener('resize', updateColorPickerPosition)
    window.addEventListener('scroll', updateColorPickerPosition, true)

    return () => {
      window.removeEventListener('resize', updateColorPickerPosition)
      window.removeEventListener('scroll', updateColorPickerPosition, true)
    }
  }, [colorPickerOpen])

  const debouncedCheckFograAdmissibility = useDebounceCallback((fograHex: string) => {
    if (gamutCheckAbortRef.current) gamutCheckAbortRef.current.abort()

    const requestId = gamutCheckRequestIdRef.current + 1
    gamutCheckRequestIdRef.current = requestId
    const abortController = new AbortController()
    gamutCheckAbortRef.current = abortController

    const run = async () => {
      const response = await convertFogra39HexToCmyk(fograHex, abortController.signal)
      if (requestId !== gamutCheckRequestIdRef.current) return
      if (!response || !response.ok) {
        setIsFogra39Admissible(null)
        return
      }
      setIsFogra39Admissible(!response.result.outOfGamut)
    }

    void run()
  }, 250)

  useEffect(() => {
    if (variant !== 'cmyk') {
      setIsFogra39Admissible(null)
      if (gamutCheckAbortRef.current) gamutCheckAbortRef.current.abort()
      return
    }

    const normalizedHex = normalizeHexColor(value)
    const colorParts = splitHexColorAndAlpha(normalizedHex)

    if (!colorParts) {
      setIsFogra39Admissible(null)
      return
    }

    debouncedCheckFograAdmissibility(applyAlphaToHexColor(colorParts.rgbHex, colorParts.alphaHex))
  }, [value, variant, debouncedCheckFograAdmissibility])

  useEffect(() => {
    return () => {
      if (gamutCheckAbortRef.current) gamutCheckAbortRef.current.abort()
    }
  }, [])

  const rasterColoriOpts = [] // TODO - mettere qui i colori già presenti per una scelta più rapida
  // Quando cambia il picker (cmyk), passiamo direttamente l'HEX
  const handleColorPickerChange = (color: any) => {
    if (isColorChangeDisabled) return

    const normalizedHex = normalizeHexColor(color?.hex || '')
    const alphaHexFromPicker = toAlphaHex(color?.rgb?.a)
    let resolvedHex =
      normalizedHex && alphaHexFromPicker && alphaHexFromPicker !== 'ff'
        ? applyAlphaToHexColor(normalizedHex, alphaHexFromPicker)
        : normalizedHex

    // GithubPicker restituisce spesso `#RRGGBB` anche quando la palette contiene
    // `#RRGGBBAA`. In modalità palette riallineiamo al valore originale allowedColor
    // così non perdiamo il canale alpha.
    if (hasLimitedPalette && normalizedHex) {
      if (!normalizedAllowedHexColors.includes(resolvedHex)) {
        const selectedParts = splitHexColorAndAlpha(resolvedHex || normalizedHex)
        const matchedAllowedHex = selectedParts
          ? normalizedAllowedHexColors.find((allowedHex) => {
              const allowedParts = splitHexColorAndAlpha(allowedHex)
              return allowedParts?.rgbHex === selectedParts.rgbHex
            })
          : null
        if (matchedAllowedHex) {
          resolvedHex = normalizeHexColor(matchedAllowedHex)
        }
      }

      if (!normalizedAllowedHexColors.includes(resolvedHex)) return
    }

    if (onChange) onChange(resolvedHex)
  }

  // Quando cambia il pantone
  const onLocalChange = (e) => {
    if (isColorChangeDisabled) return

    const normalizedValue = normalizeHexColor(e.target.value)
    if (hasLimitedPalette && !normalizedAllowedHexColors.includes(normalizedValue)) return

    if (onChange) onChange(normalizedValue)
  }

  const pantoneHexValue = useMemo(() => normalizeHexColor(value), [value])
  const cmykDisplayValue = useMemo(() => convertHexToCmyk(value), [value])
  const gamutAlertText =
    variant === 'cmyk' && isFogra39Admissible === false
      ? t(
          'I colori caricati potrebbero subire piccole modifiche per ottimizzarli ai profili di stampa. Verifica attentamente la bozza di stampa che riceverai dopo aver completato l\'ordine'
        )
      : null

  return (
    <div ref={rootRef} className={cn(styles.root, styles[`root--${variant}`], className)}>
      {variant === 'pantone' ? (
        renderColorBox()
      ) : (
        <button
          type="button"
          ref={colorBoxButtonRef}
          onClick={() => setColorPickerOpen(!colorPickerOpen)}
          disabled={isColorChangeDisabled}
        >
          {renderColorBox()}
        </button>
      )}
      <FormField
        styleVariant="editor"
        size="sm"
        label={t(variant === 'pantone' ? 'PANTONE' : 'CMYK (valore stimato)')}
        className={styles.field}
        status={variant === 'cmyk' && isFogra39Admissible === false ? 'info' : 'no-status'}
      >
        {isColorChangeDisabled ? (
          // Se è disabilitato, mostriamo una select disabilitata con l'unica opzione possibile
          <FormSelect
            value={valueAsLocked}
            options={
              lockColorChange
                ? [
                    {
                      value: valueAsLocked,
                      label: lockColorChange.nome,
                    },
                  ]
                : []
            }
            isDisabled={true}
          />
        ) : variant === 'pantone' ? (
          <PantoneSelect
            value={pantoneHexValue}
            onChange={onLocalChange}
            valueMode="hex"
            allowedColors={allowedColors}
            isDisabled={isColorChangeDisabled}
            fixedMenu
          />
        ) : (
          <button
            type="button"
            onClick={() => setColorPickerOpen(!colorPickerOpen)}
            ref={triggerRef}
            disabled={isColorChangeDisabled}
          >
            <FormInput
              id={`editorcolor_cmyk_${id}`}
              value={cmykDisplayValue} // Mostra la stringa CMYK
              readOnly
              className={cn(styles.input, styles['input--cmyk'])}
            />
          </button>
        )}
      </FormField>
      {colorPickerOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          // Render via portal per evitare stacking/overflow dello sticky editor.
          <div
            className={styles.colorPickerDropdown}
            ref={colorPickerRef}
            style={{ top: `${colorPickerPosition.top}px`, left: `${colorPickerPosition.left}px` }}
          >
            {hasLimitedPalette ? (
              <GithubColorPicker
                color={value}
                onChange={handleColorPickerChange}
                className={styles.colorPicker}
                colors={normalizedAllowedHexColors}
              />
            ) : (
              <SketchColorPicker
                color={value}
                onChange={handleColorPickerChange}
                presetColors={rasterColoriOpts.length > 0 ? rasterColoriOpts : undefined}
                className={styles.colorPicker}
                disableAlpha
                alertText={gamutAlertText}
              />
            )}
          </div>,
          document.body
        )}
    </div>
  )
}

export default EditorColor
